/* ==========================================================================
   Avalanche — header behaviour
   <av-header>            measures the header, transparent mode, solid-on-scroll, hide-on-scroll
   <av-menu-item>         dropdown / flyout / mega menu disclosure (hover or click)
   <av-menu-drawer>       menu drawer with a secondary panel for nested links
   <av-predictive-search> live search results inside the search panel
   ========================================================================== */

const root = document.documentElement;
const finePointer = () => window.matchMedia('(pointer: fine)').matches;
const desktop = () => window.matchMedia('(min-width: 1000px)').matches;
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const defineOnce = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

/* ---------------------------------------------------------------------------
   Header shell
   ------------------------------------------------------------------------- */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || this;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.lastY = window.scrollY;
    this.visible = true;
    root.style.setProperty('--header-visible', '1');

    this.sizeObserver = new ResizeObserver(() => this.measure());
    this.sizeObserver.observe(this);
    this.measure();

    this.detectTransparency();
    this.updateSolid();

    window.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    if (this.hasAttribute('hide-on-scroll')) {
      window.addEventListener('pointermove', (e) => {
        if (e.clientY < 100 && finePointer()) {
          this.setVisible(true);
          this.lastY = window.scrollY;
        }
      }, { passive: true, signal });
    }

    // The search panel sits right below the header: remember where the header ends when it opens
    this.addEventListener('overlay:open', (e) => {
      if (!e.target.classList.contains('search-panel')) return;
      e.target.style.setProperty('--search-top', `${Math.max(0, this.getBoundingClientRect().bottom)}px`);
    }, { signal });

    // Re-check the transparent mode whenever the editor changes sections
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((type) => {
      document.addEventListener(type, () => requestAnimationFrame(() => this.detectTransparency()), { signal });
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.sizeObserver?.disconnect();
    root.style.setProperty('--header-visible', '1');
  }

  measure() {
    root.style.setProperty('--header-height', `${Math.round(this.getBoundingClientRect().height)}px`);
  }

  detectTransparency() {
    const first = document.querySelector('#main > .shopify-section');
    const next = this.section.nextElementSibling;
    const blockedByGroup = next && next.classList.contains('shopify-section-group-header-group');
    const on = Boolean(first && first.querySelector('[data-transparent-header]')) && !blockedByGroup;
    this.section.classList.toggle('header--transparent', on);
  }

  get solidThreshold() {
    const bar = document.querySelector('.announcement-section');
    if (bar && (bar.compareDocumentPosition(this.section) & Node.DOCUMENT_POSITION_FOLLOWING)) {
      const sticky = getComputedStyle(root).getPropertyValue('--announcement-sticky').trim() === '1';
      if (!sticky) return Math.max(10, bar.offsetHeight);
    }
    return 10;
  }

  updateSolid() {
    this.classList.toggle('is-solid', window.scrollY > this.solidThreshold);
  }

  onScroll() {
    this.updateSolid();
    if (!this.hasAttribute('hide-on-scroll')) return;
    const y = window.scrollY;
    if (y > this.lastY && y - this.lastY > 100) {
      this.setVisible(false);
      this.lastY = y;
    } else if (y < this.lastY) {
      this.setVisible(true);
      this.lastY = y;
    }
  }

  setVisible(visible) {
    if (visible === this.visible) return;
    if (!visible && this.section.querySelector('details[open], .overlay.is-open')) return;
    this.visible = visible;
    root.style.setProperty('--header-visible', visible ? '1' : '0');
    this.section.classList.toggle('is-hidden', !visible);
  }
}

/* ---------------------------------------------------------------------------
   Menu disclosure (dropdown, nested flyout or mega menu)
   ------------------------------------------------------------------------- */
const openItems = new Set();

class MenuItem extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector(':scope > details');
    this.summary = this.details?.querySelector(':scope > summary');
    if (!this.summary) return;
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.summary.addEventListener('click', (e) => this.onSummaryClick(e), { signal });
    this.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse' || this.trigger !== 'hover') return;
      clearTimeout(this.closeTimer);
      this.open();
    }, { signal });
    this.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse' || this.trigger !== 'hover') return;
      this.closeTimer = setTimeout(() => this.close(), 250);
    }, { signal });
    this.addEventListener('focusout', (e) => {
      if (e.relatedTarget && !this.contains(e.relatedTarget)) this.close();
    }, { signal });
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.details.open) return;
      e.stopPropagation();
      this.close();
      this.summary.focus();
    }, { signal });
    document.addEventListener('click', (e) => {
      if (this.details.open && !this.contains(e.target)) this.close();
    }, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.closeTimer);
    openItems.delete(this);
  }

  get trigger() {
    return finePointer() ? this.getAttribute('trigger') || 'hover' : 'click';
  }

  onSummaryClick(e) {
    e.preventDefault();
    const href = this.summary.dataset.href;
    // With hover menus, a click on an already open item follows its link
    if (this.trigger === 'hover' && this.details.open && href && href !== '#') {
      window.location.href = href;
      return;
    }
    this.details.open && this.classList.contains('is-open') ? this.close() : this.open();
  }

  open() {
    clearTimeout(this.closeTimer);
    clearTimeout(this.hideTimer);
    // Close every other open item that is not an ancestor of this one
    openItems.forEach((item) => {
      if (item !== this && !item.contains(this)) item.close(true);
    });
    openItems.add(this);
    if (!this.details.open) this.details.open = true;
    requestAnimationFrame(() => this.classList.add('is-open'));
  }

  close(instant = false) {
    clearTimeout(this.closeTimer);
    if (!this.details.open) return;
    openItems.delete(this);
    this.querySelectorAll('av-menu-item').forEach((child) => child.close(true));
    this.classList.remove('is-open');
    clearTimeout(this.hideTimer);
    const finish = () => {
      if (!this.classList.contains('is-open')) this.details.open = false;
    };
    if (instant || reducedMotion()) finish();
    else this.hideTimer = setTimeout(finish, 300);
  }
}

/* ---------------------------------------------------------------------------
   Search: debounced requests to the predictive search endpoint
   ------------------------------------------------------------------------- */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-results]');
    this.status = this.querySelector('[data-status]');
    if (!this.input || !this.results) return;
    this.cache = new Map();
    this.controller = new AbortController();
    const { signal } = this.controller;

    const debounced = (window.Theme?.debounce || ((fn, wait) => {
      let t;
      return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); };
    }))(() => this.search(), 300);

    this.input.addEventListener('input', debounced, { signal });
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        const first = this.resultLinks()[0];
        if (first) { e.preventDefault(); first.focus(); }
      }
    }, { signal });
    this.results.addEventListener('keydown', (e) => this.onResultsKey(e), { signal });
    this.form?.addEventListener('submit', (e) => {
      if (!this.input.value.trim()) e.preventDefault();
    }, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.request?.abort();
  }

  resultLinks() {
    return [...this.results.querySelectorAll('[data-result], [role="tab"][aria-selected="true"]')]
      .filter((el) => el.getClientRects().length && !el.closest('[hidden]'));
  }

  onResultsKey(e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const links = this.resultLinks();
    const index = links.indexOf(document.activeElement);
    if (index === -1) return;
    e.preventDefault();
    if (e.key === 'ArrowDown') links[Math.min(index + 1, links.length - 1)].focus();
    else if (index === 0) this.input.focus();
    else links[index - 1].focus();
  }

  async search() {
    const term = this.input.value.trim();
    this.request?.abort();
    if (!term) {
      this.render('');
      return;
    }
    if (this.cache.has(term)) {
      this.render(this.cache.get(term));
      return;
    }
    this.request = new AbortController();
    const url = new URL(this.getAttribute('results-url') || window.Theme?.config?.routes?.predictiveSearch || '/search/suggest', window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[type]', 'product,collection,article,page,query');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][prefix]', 'last');
    this.results.setAttribute('aria-busy', 'true');
    this.classList.add('is-loading');
    try {
      const response = await fetch(url.toString(), { signal: this.request.signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const wrapper = doc.getElementById('shopify-section-predictive-search') || doc.body;
      const html = wrapper.innerHTML;
      this.cache.set(term, html);
      if (this.input.value.trim() === term) this.render(html);
    } catch (err) {
      if (err.name !== 'AbortError') this.render('');
    } finally {
      this.results.setAttribute('aria-busy', 'false');
      this.classList.remove('is-loading');
    }
  }

  render(html) {
    this.results.innerHTML = html;
    const hasContent = html.trim() !== '';
    this.classList.toggle('has-results', hasContent);
    this.input.setAttribute('aria-expanded', String(hasContent));
    if (this.status) this.status.textContent = hasContent ? this.dataset.loadedText || '' : '';
  }
}

/* ---------------------------------------------------------------------------
   Registration (the drawer extends the shared overlay from theme.js)
   ------------------------------------------------------------------------- */
defineOnce('av-header', SiteHeader);
defineOnce('av-menu-item', MenuItem);
defineOnce('av-predictive-search', PredictiveSearch);

customElements.whenDefined('av-overlay').then(() => {
  const Base = window.Theme?.Overlay || customElements.get('av-overlay');

  class MenuDrawer extends Base {
    connectedCallback() {
      super.connectedCallback();
      this.main = this.querySelector('[data-main-panel]');
      this.side = this.querySelector('[data-side-panel]');
      this.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-panel-toggle]');
        if (toggle) {
          toggle.getAttribute('aria-expanded') === 'true' && desktop() ? this.closePanel() : this.openPanel(toggle);
          return;
        }
        if (e.target.closest('[data-panel-back]')) this.closePanel(true);
      });
      this.addEventListener('overlay:close', (e) => {
        if (e.target !== this) return;
        clearTimeout(this.resetTimer);
        this.resetTimer = setTimeout(() => this.reset(), 500);
      });
      this.addEventListener('overlay:open', (e) => {
        if (e.target === this) clearTimeout(this.resetTimer);
      });
    }

    openPanel(toggle) {
      if (!this.side) return;
      const target = this.querySelector(`#${CSS.escape(toggle.getAttribute('aria-controls'))}`);
      if (!target) return;
      clearTimeout(this.panelTimer);
      this.querySelectorAll('[data-panel-toggle]').forEach((b) => b.setAttribute('aria-expanded', String(b === toggle)));
      this.querySelectorAll('[data-sub-panel]').forEach((panel) => {
        if (panel === target) return;
        panel.hidden = true;
        panel.querySelectorAll('details[open]').forEach((d) => (d.open = false));
      });
      target.hidden = false;
      this.side.classList.add('is-open');
      this.main?.classList.add('is-shifted');
      this.classList.add('has-panel');
      requestAnimationFrame(() => {
        const focusTarget = desktop() ? target.querySelector('a, summary') : target.querySelector('[data-panel-back]');
        focusTarget?.focus({ preventScroll: true });
      });
    }

    closePanel(returnFocus = false) {
      if (!this.side) return;
      const active = this.querySelector('[data-panel-toggle][aria-expanded="true"]');
      this.querySelectorAll('[data-panel-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.side.classList.remove('is-open');
      this.main?.classList.remove('is-shifted');
      this.classList.remove('has-panel');
      clearTimeout(this.panelTimer);
      this.panelTimer = setTimeout(() => {
        if (this.side.classList.contains('is-open')) return;
        this.querySelectorAll('[data-sub-panel]').forEach((panel) => (panel.hidden = true));
      }, 300);
      if (returnFocus) active?.focus({ preventScroll: true });
    }

    reset() {
      if (this.isOpen) return;
      this.closePanel();
      this.querySelectorAll('details[open]').forEach((d) => (d.open = false));
    }
  }

  defineOnce('av-menu-drawer', MenuDrawer);
});
